import { useMemo } from 'react';
import { useSelectOrFail, useSelector } from '@lingoda/hooks';
import { trans } from '@lingoda/i18n';
import { Box, Container, Link, Suspense, useResolution } from '@lingoda/ui';
import { styled } from '@lingoda/ui/theme';
import { ArrowLeftIcon } from '@lingoda/ui/icons/lingodaIcons';
import { sectionNameSelector } from '@lingoda/auth';
import { useGoBack, useParams } from '@lingoda/router';
import { actionClick, useUiEvent } from '@lingoda/analytics';
import { studentCurriculumIdSelector } from '@lingoda/students';
import { useUserSubscriptionsQuery } from '@lingoda/monolith-graphql';
import { usePaymentCurrencies } from '@lingoda/payments';
import { StoreContextProvider } from '../../components/StoreContext';
import StoreFrontContent from './Content';

const Wrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{
    isMobile: boolean;
}>(({ isMobile }) => ({
    backgroundColor: isMobile ? 'white' : 'transparent',
}));

interface StoreFrontProps {
    showBackButton?: boolean;
    showTrialBanner?: boolean;
    onCreatePackage?: ({ purchaseId }: { purchaseId: string }) => void;
}

const StoreFront = ({
    showBackButton = true,
    showTrialBanner = true,
    onCreatePackage,
}: StoreFrontProps) => {
    const sectionName = useSelector(sectionNameSelector);
    const { defaultCurrency: userCurrency } = usePaymentCurrencies();
    const curriculumId = useSelectOrFail(studentCurriculumIdSelector);
    const isMobile = useResolution('phone');

    const { data } = useUserSubscriptionsQuery({
        fetchPolicy: 'cache-and-network',
    });

    const userHasOrHadSubscriptions = useMemo(() => {
        if (!data?.subscriptions) {
            return false;
        }
        const { currentSubscription, latestSubscription, nextSubscription } = data.subscriptions;

        return !!(currentSubscription || latestSubscription || nextSubscription);
    }, [data?.subscriptions]);

    const { section: desiredSectionName, curriculum: curriculumString } = useParams<{
        section: SectionName;
        curriculum: string;
    }>();
    const desiredCurriculumId = curriculumString ? parseInt(curriculumString) : curriculumId;
    const isLearningLanguageChange = !!desiredSectionName;
    const { canGoBack, goBack } = useGoBack();
    const addEvent = useUiEvent();

    const isTrialAvailable = !userHasOrHadSubscriptions || !!desiredSectionName;

    const handleBack = () => {
        void addEvent(actionClick('Go back'));
        goBack();
    };

    return (
        <Wrapper isMobile={isMobile} py={2} data-cy="StoreFront">
            <Container>
                {canGoBack && showBackButton && (
                    <Box mb={2}>
                        <Link component="button" underline="none" onClick={handleBack}>
                            <Box py={1} display="inline-flex" alignItems="center" gap={1}>
                                <ArrowLeftIcon size={24} />
                                {trans('back', {}, 'student-store')}
                            </Box>
                        </Link>
                    </Box>
                )}
                <Suspense variant="circular">
                    {sectionName && (
                        <StoreContextProvider
                            sectionName={desiredSectionName ?? sectionName}
                            defaultCurrency={userCurrency}
                            defaultCurriculumId={desiredCurriculumId ?? curriculumId}
                            isLearningLanguageChange={isLearningLanguageChange}
                        >
                            <StoreFrontContent
                                sectionName={desiredSectionName ?? sectionName}
                                isTrialAvailable={isTrialAvailable}
                                showTrialBanner={showTrialBanner}
                                isLearningLanguageChange={isLearningLanguageChange}
                                onCreatePackage={onCreatePackage}
                            />
                        </StoreContextProvider>
                    )}
                </Suspense>
            </Container>
        </Wrapper>
    );
};

export default StoreFront;

export { StoreContextProvider };
